"use client";
import { useState, useEffect } from "react";
import {
  Paginator,
  useBetaDataTableContext as useTableContext,
} from "@zapier/design-system";

import styles from "./Pagination.module.css";
import { PaginationCount } from "./PaginationCount";
import { useRouterParams } from "../../../../hooks/useRouterParams";
import { PaginationSelect } from "./PaginationSelect";
import { usePageSize } from "../../../../context/PageSizeContext";
import { ZAP_PAGE_SIZE } from "@zapier/utils";

interface Props {
  page: number | null;
  totalPages: number;
  totalCount?: number;
  isLoading?: boolean;
  allowPageSizeChange?: boolean;
}

function Pagination({
  page: initialPage,
  totalPages,
  totalCount = 0,
  isLoading = false,
  allowPageSizeChange = true,
}: Props) {
  const [page, setPage] = useState<number>(initialPage || 1);
  const { page: parameterPage, setParams } = useRouterParams();
  const { clearSelection } = useTableContext();
  const { size: _pageSize } = usePageSize();

  // only check for different page sizes if that feature is enabled
  const pageSize = allowPageSizeChange ? _pageSize : ZAP_PAGE_SIZE;

  // Make sure that the local page state is reset if page is changed from somewhere else
  useEffect(() => {
    const hasDesiredPage =
      parameterPage === initialPage || (!parameterPage && initialPage === 1);
    const isOnDesiredPage = page === parameterPage;

    if (hasDesiredPage && !isOnDesiredPage) {
      setPage(parameterPage || 1);
    }
  }, [initialPage, parameterPage, page]);

  const onPaginate = ({ pageIndex }: { pageIndex: number }) => {
    setPage(pageIndex + 1);
    setParams({ page: pageIndex + 1 });
    // scroll to top
    if (document) {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }
    // Clear selected entities when paginating
    clearSelection();
  };

  if (isLoading) {
    return;
  }

  return (
    <div className={styles.paginator}>
      <div>
        {!!totalCount && (
          <PaginationCount
            page={page}
            totalCount={totalCount}
            pageSize={pageSize}
          />
        )}
      </div>
      <div>
        {totalPages > 1 && (
          <Paginator
            condenseAt={8}
            currentPageIndex={page - 1}
            pageUrls={Array.from({ length: totalPages }).map(() => "")}
            isCentered={true}
            onPaginate={onPaginate}
          />
        )}
      </div>
      <div>{allowPageSizeChange && !!totalCount && <PaginationSelect />}</div>
    </div>
  );
}

export default Pagination;
